import { useEffect, useState } from "react";
import { crmSupabase } from "../../lib/crm-client";
import { getSetting, setSetting } from "../../lib/crm-mutations";

type Category = {
  id: string;
  name: string;
  slug: string;
  color_hex: string;
  target_volume: number;
  parent_id: string | null;
};

export default function CrmSettings() {
  const [cats, setCats] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [assistantEmail, setAssistantEmail] = useState("");
  const [assistantName, setAssistantName] = useState("");
  const [savedAt, setSavedAt] = useState<string | null>(null);

  useEffect(() => {
    if (!crmSupabase) {
      setLoading(false);
      return;
    }
    Promise.all([
      crmSupabase
        .from("category")
        .select("id,name,slug,color_hex,target_volume,parent_id")
        .order("sort_order", { ascending: true }),
      getSetting("assistant_email"),
      getSetting("assistant_name"),
    ]).then(([catsRes, emailVal, nameVal]) => {
      if (catsRes.error) console.error(catsRes.error);
      setCats((catsRes.data as Category[]) ?? []);
      setAssistantEmail(emailVal ?? "eitan@llyll.com");
      setAssistantName(nameVal ?? "Ethan");
      setLoading(false);
    });
  }, []);

  const handleSaveAssistant = async () => {
    try {
      await setSetting("assistant_email", assistantEmail.trim());
      await setSetting("assistant_name", assistantName.trim());
      setSavedAt(new Date().toLocaleTimeString("fr-FR"));
    } catch (e) {
      console.error(e);
      alert("Erreur sauvegarde");
    }
  };

  if (loading) {
    return (
      <main className="px-10 py-8 max-w-[1200px] mx-auto font-mono text-sm text-concrete">
        Chargement…
      </main>
    );
  }

  const topLevel = cats.filter((c) => !c.parent_id);
  const childrenOf = (id: string) => cats.filter((c) => c.parent_id === id);

  return (
    <main className="px-10 py-8 max-w-[1200px] mx-auto">
      <header className="mb-6">
        <h2 className="font-sans text-4xl font-bold tracking-tight text-charcoal">
          Réglages
        </h2>
      </header>

      <section className="mb-8 bg-white border border-stone rounded-lg p-4">
        <h3 className="font-sans text-lg font-bold mb-3 text-charcoal">
          Assistant
        </h3>
        <p className="font-mono text-[11px] text-concrete mb-3">
          Personne qui reçoit les notes "envoi mail" depuis les fiches lead.
        </p>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <label className="flex flex-col gap-1">
            <span className="font-mono text-[10px] uppercase text-concrete">
              Nom affiché
            </span>
            <input
              type="text"
              value={assistantName}
              onChange={(e) => setAssistantName(e.target.value)}
              className="px-3 py-2 bg-ivory border border-stone rounded text-sm focus:outline-none focus:border-charcoal"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="font-mono text-[10px] uppercase text-concrete">
              Adresse email
            </span>
            <input
              type="email"
              value={assistantEmail}
              onChange={(e) => setAssistantEmail(e.target.value)}
              className="px-3 py-2 bg-ivory border border-stone rounded text-sm font-mono focus:outline-none focus:border-charcoal"
            />
          </label>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleSaveAssistant}
            className="px-3 py-1.5 bg-charcoal text-ivory rounded text-xs font-mono uppercase hover:bg-black"
          >
            Sauvegarder
          </button>
          {savedAt && (
            <span className="font-mono text-[10px] text-green-700">
              ✓ sauvegardé à {savedAt}
            </span>
          )}
        </div>
      </section>

      <section className="mb-8">
        <h3 className="font-sans text-lg font-bold mb-3 text-charcoal">
          Catégories ({cats.length})
        </h3>
        <ul className="flex flex-col gap-2">
          {topLevel.map((cat) => {
            const kids = childrenOf(cat.id);
            return (
              <li
                key={cat.id}
                className="bg-white border border-stone rounded-lg p-3"
              >
                <div className="flex items-center gap-3">
                  <span
                    className="inline-block w-4 h-4 rounded"
                    style={{ backgroundColor: cat.color_hex }}
                  />
                  <span className="font-semibold text-sm text-charcoal">
                    {cat.name}
                  </span>
                  <span className="font-mono text-[10px] uppercase text-concrete">
                    target {cat.target_volume}
                  </span>
                  {kids.length > 0 && (
                    <span className="font-mono text-[10px] uppercase text-ash">
                      {kids.length} sous-cat.
                    </span>
                  )}
                </div>
                {kids.length > 0 && (
                  <ul className="mt-2 ml-7 flex flex-col gap-1">
                    {kids.map((kid) => (
                      <li
                        key={kid.id}
                        className="flex items-center gap-3 text-xs"
                      >
                        <span
                          className="inline-block w-3 h-3 rounded"
                          style={{ backgroundColor: kid.color_hex }}
                        />
                        <span className="text-charcoal">{kid.name}</span>
                        <span className="font-mono text-[10px] uppercase text-concrete">
                          target {kid.target_volume}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </section>

      <section className="p-4 bg-stone rounded-lg">
        <p className="font-mono text-xs text-charcoal mb-2">
          📌 Connexion Supabase
        </p>
        <p className="font-mono text-[10px] text-concrete">
          Project : videolink-prod · Schéma : crm · Région : us-east-1 · Plan :
          Free
        </p>
      </section>
    </main>
  );
}
